<template>
  <div
    v-if="toasts.length"
    class="toast-stack"
  >
    <!-- Clear all button when 3+ toasts -->
    <div
      v-if="toasts.length >= 3"
      class="toast-stack-actions d-flex justify-end mb-1"
    >
      <v-btn
        x-small
        text
        class="text-none white--text"
        @click="clearAll"
      >
        <font-awesome-icon
          :icon="['fas', 'times']"
          size="xs"
          class="mr-1"
        />
        Clear all
      </v-btn>
    </div>

    <transition-group
      name="toast"
      tag="div"
    >
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="toast-item d-flex align-center"
        :class="statusClass(toast.status)"
      >
        <font-awesome-icon
          :icon="statusIcon(toast.status)"
          :spin="toast.status === 'loading'"
          size="sm"
          class="toast-icon mr-2 flex-shrink-0"
        />

        <span class="toast-message flex-grow-1">{{ toast.message }}</span>

        <v-btn
          icon
          x-small
          class="toast-close flex-shrink-0 ml-2"
          @click="dismiss(toast.id)"
        >
          <font-awesome-icon
            :icon="['fas', 'times']"
            size="xs"
          />
        </v-btn>
      </div>
    </transition-group>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapState: mapToolsState } = createNamespacedHelpers('tools')

const AUTO_DISMISS_MS = {
  info: 4000,
  success: 5000,
  warning: 8000,
  loading: 0,
  error: 0,
}

export default {
  name: 'ToastStack',
  data() {
    return {
      timers: {},
    }
  },
  computed: {
    ...mapToolsState({
      toasts: (state) => state.toasts,
    }),
  },
  watch: {
    toasts(newToasts, oldToasts) {
      // Set auto-dismiss timers for new toasts
      const oldIds = new Set((oldToasts || []).map((t) => t.id))
      newToasts.forEach((toast) => {
        if (!oldIds.has(toast.id) && !this.timers[toast.id]) {
          this.scheduleAutoDismiss(toast)
        }
      })

      // Clean up timers for removed toasts
      const newIds = new Set(newToasts.map((t) => t.id))
      Object.keys(this.timers).forEach((id) => {
        if (!newIds.has(Number(id))) {
          clearTimeout(this.timers[id])
          delete this.timers[id]
        }
      })
    },
  },
  beforeDestroy() {
    Object.values(this.timers).forEach((timer) => clearTimeout(timer))
  },
  methods: {
    statusIcon(status) {
      const icons = {
        info: ['fas', 'circle-info'],
        success: ['fas', 'circle-check'],
        error: ['fas', 'circle-exclamation'],
        warning: ['fas', 'triangle-exclamation'],
        loading: ['fas', 'spinner'],
      }
      return icons[status] || icons.info
    },

    statusClass(status) {
      return `toast-${status || 'info'}`
    },

    scheduleAutoDismiss(toast) {
      const delay = AUTO_DISMISS_MS[toast.status] || AUTO_DISMISS_MS.info
      if (delay > 0) {
        this.timers[toast.id] = setTimeout(() => {
          this.dismiss(toast.id)
          delete this.timers[toast.id]
        }, delay)
      }
    },

    dismiss(toastId) {
      if (this.timers[toastId]) {
        clearTimeout(this.timers[toastId])
        delete this.timers[toastId]
      }
      this.$store.commit('tools/removeToast', toastId)
    },

    clearAll() {
      Object.values(this.timers).forEach((timer) => clearTimeout(timer))
      this.timers = {}
      this.$store.commit('tools/clearToasts')
    },
  },
}
</script>

<style scoped lang="scss">
.toast-stack {
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 210;
  max-width: 380px;
  width: calc(100vw - 32px);
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
}

.toast-item {
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 6px;
  color: #ffffff;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.toast-icon {
  opacity: 0.9;
}

.toast-message {
  word-break: break-word;
}

.toast-close {
  color: rgba(255, 255, 255, 0.7) !important;
}

.toast-close:hover {
  color: #ffffff !important;
}

.toast-info {
  background-color: #37474f;
}

.toast-success {
  background-color: #2e7d32;
}

.toast-error {
  background-color: #c62828;
}

.toast-warning {
  background-color: #e65100;
}

.toast-loading {
  background-color: #1565c0;
}

/* Transition animations */
.toast-enter-active {
  transition: all 0.3s ease-out;
}

.toast-leave-active {
  transition: all 0.2s ease-in;
}

.toast-enter {
  opacity: 0;
  transform: translateX(40px);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(40px);
}

.toast-move {
  transition: transform 0.3s ease;
}

/* Mobile: full width at bottom */
@media (max-width: 599px) {
  .toast-stack {
    bottom: 8px;
    right: 8px;
    left: 8px;
    max-width: none;
    width: auto;
  }

  .toast-item {
    font-size: 12px;
    padding: 8px 10px;
  }
}
</style>
